/**
 * The result of a photographed page.
 *
 * The transcript, as the model read it, and the mistakes below it. Nothing is
 * corrected here — this is the look at what was found and the way to bring
 * the text onto the paper.
 */

import { signal } from '@preact/signals'
import * as st from '../state.js'
import { Panel, Action } from './Panel.js'
import { Icon } from './icons.js'
import type { RawMistake } from '../types.js'

/** What stands in the card right now. */
type PhotoState =
  /** Reading page `nr` of `total` — one of one, most of the time. */
  | { kind: 'wait'; nr: number; total: number }
  | { kind: 'error'; message: string }
  | { kind: 'done'; text: string; sure: RawMistake[]; unsure: number; stored: number }

export const photo = signal<PhotoState | null>(null)

/** The kinds as Elmar would read them. */
const KIND_LABEL: Record<string, string> = {
  'gross-klein': 'groß/klein',
  'dehnungs-h': 'Dehnungs-h',
  'doppelkonsonant': 'doppelter Buchstabe',
  'ie-i': 'ie oder i',
  's-ss-ß': 's, ss oder ß',
  'd-t': 'd oder t',
  'umlaut': 'Umlaut',
  'getrennt-zusammen': 'getrennt oder zusammen',
  'endung': 'Endung',
  'verwechselt': 'verwechselt',
  'nach-gehoer': 'nach Gehör',
}

export function PhotoPanel({
  onTakeOver,
  onRetry,
}: {
  /** Bring the transcript onto the paper. */
  onTakeOver: (text: string) => void
  onRetry: () => void
}) {
  const p = photo.value
  const open = st.panel.value === 'photo' && p !== null
  const close = (): void => {
    st.panel.value = null
    photo.value = null
  }

  if (!p) return null

  return (
    // No anchor: the card answers a photograph taken a screen ago, not a
    // button standing here. It belongs in the middle.
    <Panel open={open} title={titleFor(p)} onClose={close}
           actions={actionsFor(p, close, onTakeOver, onRetry)}>
      <div class="photo-body">
        {p.kind === 'wait' && (
          <p class="photo-wait">
            {p.total > 1
              ? `Seite ${p.nr} von ${p.total}. Jede dauert etwa zwanzig Sekunden.`
              : 'Das dauert etwa zwanzig Sekunden.'}
          </p>
        )}

        {p.kind === 'error' && <p class="photo-error">{p.message}</p>}

        {p.kind === 'done' && (
          <>
            <div class="photo-text">
              {p.text.split('\n').map((l, i) => (
                // An empty line needs content, otherwise it collapses and
                // the transcript loses its paragraphs.
                <div key={i}>{l || ' '}</div>
              ))}
            </div>

            {p.sure.length ? (
              <>
                <p class="docs-note">
                  {p.stored} Fehler gefunden
                  {p.unsure
                    ? ` — ${p.unsure} ${p.unsure === 1 ? 'Wort' : 'Wörter'} unsicher gelesen, die zählen nicht.`
                    : '.'}
                </p>
                <ul class="photo-list">
                  {p.sure.map((m, i) => (
                    <li key={i}>
                      <span class="words-wrong">{m.wrong}</span>
                      <span class="words-arrow">
                        <Icon name="arrow-right" size={14} />
                      </span>
                      <span class="words-right">{m.right}</span>
                      <span class="photo-kind">{KIND_LABEL[m.kind] ?? m.kind}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p class="docs-note">Keine Rechtschreibfehler gefunden.</p>
            )}
          </>
        )}
      </div>
    </Panel>
  )
}

function titleFor(p: PhotoState): string {
  if (p.kind === 'wait') return p.total > 1 ? `Lese Seite ${p.nr} von ${p.total} …` : 'Lese die Seite …'
  if (p.kind === 'error') return 'Das hat nicht geklappt'
  return p.sure.length ? `${p.sure.length} Fehler` : 'Alles richtig'
}

function actionsFor(
  p: PhotoState,
  close: () => void,
  onTakeOver: (text: string) => void,
  onRetry: () => void
) {
  if (p.kind === 'wait') return undefined
  if (p.kind === 'error') {
    return (
      <Action onClick={() => { close(); onRetry() }}>Noch einmal</Action>
    )
  }
  return (
    <>
      <Action icon="file-text" onClick={() => { close(); onTakeOver(p.text) }}>
        Als Text übernehmen
      </Action>
      <Action onClick={close}>Schließen</Action>
    </>
  )
}
